import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import { formatDistanceToNow } from 'date-fns';
import {
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  Eye,
  Loader2,
  Mail,
  Paperclip,
  X,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import { Button } from '@/components/ui/catalyst/button';
import { cn } from '@/lib/utils';
import {
  FrontConversationResponse,
  FrontMessageAttachment,
  FrontMessageDetail,
  useGetFrontConversationByDbIdQuery,
  useGetFrontConversationQuery,
} from '@/services/api/frontMessageService';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface FrontEmailThreadViewerProps {
  matterId: string;
  /** Front conversation ID (cnv_xxx format) - use this OR conversationDbId */
  conversationId?: string;
  /** Database UUID for the conversation - use this OR conversationId */
  conversationDbId?: string;
  /** Message ID to highlight/scroll to */
  highlightMessageId?: string;
  /** Optional pre-loaded conversation data to avoid fetching */
  conversationData?: FrontConversationResponse | undefined;
}

// Format file size helper
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Get file type icon
const getFileTypeIcon = (contentType: string): string => {
  if (contentType.startsWith('image/')) return '🖼️';
  if (contentType.includes('pdf')) return '📄';
  if (contentType.includes('word') || contentType.includes('document'))
    return '📝';
  if (contentType.includes('excel') || contentType.includes('spreadsheet'))
    return '📊';
  if (contentType.includes('zip') || contentType.includes('compressed'))
    return '📦';
  return '📎';
};

// Check if attachment is previewable
const isPreviewable = (contentType: string): boolean => {
  return (
    contentType.includes('pdf') ||
    contentType.startsWith('image/') ||
    contentType.includes('word') ||
    contentType.includes('document')
  );
};

// Helper to download file with auth
const downloadWithAuth = async (url: string, filename: string) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) throw new Error('Download failed');

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download error:', error);
  }
};

// Attachment item component
const AttachmentItem: React.FC<{
  attachment: FrontMessageAttachment;
  matterId: string;
  messageId: string;
  onPreview: (url: string, filename: string, contentType: string) => void;
}> = ({ attachment, matterId, messageId, onPreview }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadUrl = `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/front/messages/${messageId}/attachments/${attachment.id}/download`;
  const canPreview = isPreviewable(attachment.content_type);
  const icon = getFileTypeIcon(attachment.content_type);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadWithAuth(downloadUrl, attachment.filename);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-2 bg-white border rounded-md px-3 py-2 text-sm">
      <span>{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="truncate font-medium text-gray-900">
          {attachment.filename}
        </div>
        <div className="text-xs text-gray-500">
          {formatFileSize(attachment.size)}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {canPreview && (
          <button
            type="button"
            onClick={() =>
              onPreview(
                downloadUrl,
                attachment.filename,
                attachment.content_type,
              )
            }
            className="p-1.5 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900"
            title="Preview"
          >
            <Eye className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={handleDownload}
          disabled={isDownloading}
          className="p-1.5 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900 disabled:opacity-50"
          title="Download"
        >
          {isDownloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
};

// Single email message component
const EmailMessage: React.FC<{
  message: FrontMessageDetail;
  matterId: string;
  isExpanded: boolean;
  isHighlighted: boolean;
  isLast: boolean;
  onToggle: () => void;
  onPreviewAttachment: (
    url: string,
    filename: string,
    contentType: string,
  ) => void;
}> = ({
  message,
  matterId,
  isExpanded,
  isHighlighted,
  isLast,
  onToggle,
  onPreviewAttachment,
}) => {
  const [showQuoted, setShowQuoted] = useState(false);

  const timeAgo = message.created_at
    ? formatDistanceToNow(new Date(message.created_at), { addSuffix: true })
    : null;

  const senderName = message.author
    ? `${message.author.first_name || ''} ${message.author.last_name || ''}`.trim() ||
      message.author.email
    : message.is_inbound
      ? 'External'
      : 'Unknown';

  const senderEmail = message.author?.email || '';

  // Process body to handle quoted text
  const processedBody = useMemo(() => {
    if (!message.body) return { html: '', hasQuotes: false };

    const parser = new DOMParser();
    const doc = parser.parseFromString(message.body, 'text/html');
    const quotedElements = doc.querySelectorAll(
      'blockquote, .gmail_quote, [class*="quote"]',
    );
    const hasQuotes = quotedElements.length > 0;

    if (!showQuoted && hasQuotes) {
      quotedElements.forEach((el) => el.remove());
    }

    return { html: doc.body.innerHTML, hasQuotes };
  }, [message.body, showQuoted]);

  const hasAttachments = message.attachments && message.attachments.length > 0;

  return (
    <div
      className={cn(
        'bg-white border rounded-lg shadow-sm overflow-hidden',
        isHighlighted && 'ring-2 ring-blue-400',
        message.is_inbound
          ? 'border-l-4 border-l-blue-400'
          : 'border-l-4 border-l-gray-300',
      )}
    >
      {/* Message Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full p-3 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-start gap-3">
          <Mail className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-medium text-sm text-gray-900 truncate">
                  {senderName}
                </span>
                {senderEmail && (
                  <span className="text-xs text-gray-500 truncate hidden sm:inline">
                    &lt;{senderEmail}&gt;
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span
                  className={cn(
                    'text-[10px] font-medium px-1.5 py-0.5 rounded',
                    message.is_inbound
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600',
                  )}
                >
                  {message.is_inbound ? 'IN' : 'OUT'}
                </span>
                {timeAgo && (
                  <span className="text-xs text-gray-500">{timeAgo}</span>
                )}
                {isExpanded || isLast ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
              </div>
            </div>
            {message.subject && (
              <div className="text-sm text-gray-700 mt-0.5 truncate">
                {message.subject}
              </div>
            )}
            {!(isExpanded || isLast) && message.blurb && (
              <div className="text-xs text-gray-500 mt-1 truncate">
                {message.blurb}
              </div>
            )}
          </div>
        </div>
      </button>

      {/* Message Body */}
      {(isExpanded || isLast) && (
        <div className="border-t bg-gray-50/50 p-4 space-y-3">
          {/* Email Content */}
          <div className="prose prose-sm max-w-none">
            {processedBody.html ? (
              <div
                className="text-sm text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: processedBody.html }}
              />
            ) : message.text ? (
              <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {message.text}
              </div>
            ) : (
              <div className="text-sm text-gray-500 italic">
                No content available
              </div>
            )}
          </div>

          {/* Quoted Text Toggle */}
          {processedBody.hasQuotes && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowQuoted(!showQuoted);
              }}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              {showQuoted ? '▲ Hide quoted text' : '▼ Show quoted text'}
            </button>
          )}

          {/* Attachments */}
          {hasAttachments && (
            <div className="pt-2 border-t">
              <div className="flex items-center gap-1 text-xs font-medium text-gray-700 mb-2">
                <Paperclip className="h-3 w-3" />
                <span>
                  {message.attachments.length} attachment
                  {message.attachments.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="space-y-2">
                {message.attachments.map((att) => (
                  <AttachmentItem
                    key={att.id}
                    attachment={att}
                    matterId={matterId}
                    messageId={message.id}
                    onPreview={onPreviewAttachment}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Attachment preview panel
const AttachmentPreview: React.FC<{
  url: string;
  filename: string;
  contentType: string;
  onClose: () => void;
}> = ({ url, filename, contentType, onClose }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);

  const isPDF = contentType.includes('pdf');
  const isImage = contentType.startsWith('image/');
  const isWord =
    contentType.includes('word') || contentType.includes('document');

  const onDocumentLoadSuccess = ({ numPages: pages }: { numPages: number }) => {
    setNumPages(pages);
  };

  // Fetch file with auth and create blob URL
  React.useEffect(() => {
    let objectUrl: string | null = null;

    const fetchFile = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setNumPages(null);
        const token = localStorage.getItem('access_token');
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load file: ${response.status}`);
        }

        const fetchedBlob = await response.blob();
        // Ensure PDF blobs have the correct MIME type
        const typedBlob = isPDF
          ? new Blob([fetchedBlob], { type: 'application/pdf' })
          : fetchedBlob;
        objectUrl = window.URL.createObjectURL(typedBlob);
        setBlobUrl(objectUrl);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load file');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFile();

    // Cleanup blob URL on unmount
    return () => {
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [url, isPDF]);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadWithAuth(url, filename);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOpenInNewTab = () => {
    if (blobUrl) {
      window.open(blobUrl, '_blank');
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Preview Header */}
      <div className="flex items-center justify-between p-3 border-b bg-gray-50">
        <div className="flex items-center gap-2 min-w-0">
          <span>{getFileTypeIcon(contentType)}</span>
          <span className="font-medium text-sm text-gray-900 truncate">
            {filename}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleDownload}
            disabled={isDownloading}
            className="p-1.5 hover:bg-gray-200 rounded text-gray-600 disabled:opacity-50"
            title="Download"
          >
            {isDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
          </button>
          <button
            type="button"
            onClick={handleOpenInNewTab}
            disabled={!blobUrl}
            className="p-1.5 hover:bg-gray-200 rounded text-gray-600 disabled:opacity-50"
            title="Open in new tab"
          >
            <ExternalLink className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 hover:bg-gray-200 rounded text-gray-600"
            title="Close preview"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Preview Content */}
      <div className="flex-1 overflow-hidden">
        {isLoading && (
          <div className="w-full h-full flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        )}
        {error && (
          <div className="w-full h-full flex items-center justify-center text-red-500">
            {error}
          </div>
        )}
        {!isLoading && !error && blobUrl && isPDF && (
          <div className="w-full h-full overflow-auto bg-gray-100 p-4">
            <Document
              file={blobUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              }
              error={
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <p>Failed to load PDF</p>
                  <button
                    type="button"
                    onClick={handleOpenInNewTab}
                    className="mt-2 text-blue-600 hover:underline"
                  >
                    Open in new tab
                  </button>
                </div>
              }
            >
              {numPages &&
                Array.from(new Array(numPages), (_, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    width={500}
                    className="mb-4 shadow-md"
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                ))}
            </Document>
          </div>
        )}
        {!isLoading && !error && blobUrl && isImage && (
          <div className="w-full h-full flex items-center justify-center p-4 bg-gray-100">
            <img
              src={blobUrl}
              alt={filename}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        )}
        {!isLoading && !error && isWord && (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 p-4">
            <p>Word document preview is not available.</p>
            <button
              type="button"
              onClick={handleDownload}
              className="mt-2 text-blue-600 hover:underline"
            >
              Download to view
            </button>
          </div>
        )}
        {!isLoading && !error && !isPDF && !isImage && !isWord && (
          <div className="w-full h-full flex items-center justify-center text-gray-500">
            Preview not available for this file type
          </div>
        )}
      </div>
    </div>
  );
};

export const FrontEmailThreadViewer: React.FC<FrontEmailThreadViewerProps> = ({
  matterId,
  conversationId,
  conversationDbId,
  highlightMessageId,
  conversationData: preloadedData,
}) => {
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(
    new Set(),
  );
  const [previewAttachment, setPreviewAttachment] = useState<{
    url: string;
    filename: string;
    contentType: string;
  } | null>(null);

  // Fetch by Front conversation ID (cnv_xxx format)
  const {
    data: fetchedByConvId,
    isLoading: isLoadingByConvId,
    error: errorByConvId,
  } = useGetFrontConversationQuery(
    { matterId, conversationId: conversationId ?? '' },
    { skip: !matterId || !conversationId || !!preloadedData || !!conversationDbId },
  );

  // Fetch by database UUID
  const {
    data: fetchedByDbId,
    isLoading: isLoadingByDbId,
    error: errorByDbId,
  } = useGetFrontConversationByDbIdQuery(
    { matterId, conversationDbId: conversationDbId ?? '' },
    { skip: !matterId || !conversationDbId || !!preloadedData },
  );

  // Combine loading/error states
  const isLoading = isLoadingByConvId || isLoadingByDbId;
  const error = errorByConvId || errorByDbId;
  const fetchedData = fetchedByConvId || fetchedByDbId;

  // Use preloaded data if available, otherwise use fetched data
  const data = preloadedData || fetchedData;

  // Expand highlighted message and last message by default
  React.useEffect(() => {
    if (data?.messages && data.messages.length > 0) {
      const initialExpanded = new Set<string>();
      const lastMessageId = data.messages[data.messages.length - 1].id;
      initialExpanded.add(lastMessageId);
      if (highlightMessageId) {
        initialExpanded.add(highlightMessageId);
      }
      setExpandedMessages(initialExpanded);
    }
  }, [data?.messages, highlightMessageId]);

  const toggleMessage = (messageId: string) => {
    setExpandedMessages((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  const handlePreviewAttachment = (
    url: string,
    filename: string,
    contentType: string,
  ) => {
    setPreviewAttachment({ url, filename, contentType });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading conversation...</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center h-full text-red-500">
        Failed to load conversation
      </div>
    );
  }

  const { conversation, messages } = data;

  return (
    <div className="flex h-full overflow-hidden">
      {/* Messages Panel */}
      <div
        className={cn(
          'flex flex-col h-full overflow-hidden transition-all',
          previewAttachment ? 'w-1/2' : 'w-full',
        )}
      >
        {/* Conversation Header */}
        <div className="p-4 border-b bg-white">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 truncate">
                {conversation.subject || '(No subject)'}
              </h3>
              {conversation.recipient && (
                <p className="text-sm text-gray-600 truncate mt-0.5">
                  {conversation.recipient.name || conversation.recipient.handle}
                </p>
              )}
            </div>
            <Button
              plain
              onClick={() => {
                window.open(
                  `https://app.frontapp.com/open/${conversation.id}`,
                  '_blank',
                );
              }}
              className="flex items-center gap-1 text-sm"
            >
              <ExternalLink className="h-4 w-4" />
              Open in Front
            </Button>
          </div>
        </div>

        {/* Messages List */}
        <div className="flex-1 overflow-auto p-4 bg-gray-100 space-y-3">
          {messages.map((msg, index) => (
            <EmailMessage
              key={msg.id}
              message={msg}
              matterId={matterId}
              isExpanded={expandedMessages.has(msg.id)}
              isHighlighted={msg.id === highlightMessageId}
              isLast={index === messages.length - 1}
              onToggle={() => toggleMessage(msg.id)}
              onPreviewAttachment={handlePreviewAttachment}
            />
          ))}
        </div>
      </div>

      {/* Attachment Preview Panel */}
      {previewAttachment && (
        <div className="w-1/2 border-l">
          <AttachmentPreview
            url={previewAttachment.url}
            filename={previewAttachment.filename}
            contentType={previewAttachment.contentType}
            onClose={() => setPreviewAttachment(null)}
          />
        </div>
      )}
    </div>
  );
};

export default FrontEmailThreadViewer;
